// TV Dashboard — an AltspaceVR-style console home. Experiment, mounted at /tv
// alongside the regular dashboard rather than replacing it.
//
// The shape is a living-room menu: an ambient vista running full-bleed behind
// a single centred pane, one rail of channels (tabs) across the top, and a
// horizontally focused row of cards below. Everything is drivable from the
// keyboard alone — arrows move, Enter joins — because that is the grammar of
// a TV interface, and it is also what makes it feel like one.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDown, ArrowUp, BookOpen, Box, Bug, Crown, FileText, Globe, Hammer, Lock, LogOut, Mail, MoreHorizontal, Play, Plus,
  LifeBuoy, Shield, Shirt, Star, Store, Tag, Terminal, Trash2, Upload, User as UserIcon, Users, Wallet, DraftingCompass, Gift, Heart, CalendarDays, Headset } from 'lucide-react';
import { useWorldPresence } from '../hooks/useWorldPresence';
import { unreadCount } from '../services/guestbookService';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { MARKETPLACE_PURCHASES_ENABLED } from '../config/marketplace';
import { fetchExperiences, experienceMeta, type ExperienceWorld } from '../services/experienceService';
import { createWorldWithEnvironment, listMyEnvironments, uploadEnvironment, type MyEnvironment } from '../services/newWorldFromEnvironment';
import { fetchMyAvatars, type Avatar } from '../services/avatarService';
import TVSkyBackdrop from '../components/TVSkyBackdrop';
import PrivateWorldGate from '../components/PrivateWorldGate';
import ClassBanner from '../components/classes/ClassBanner';
import { incomingRequestCount } from '../services/social';
// "Share meetup" (world card menu): only loaded when someone opens it.
const MeetupDialog = React.lazy(() => import('../components/social/MeetupDialog'));
// Games tab: leaderboards for every registered game (loaded on first open).
const GamesBoard = React.lazy(() => import('../components/games/GamesBoard'));
import './TVDashboard.css';

interface WorldRow {
  id: string;
  name: string;
  thumbnail_url?: string | null;
  owner_id: string;
  settings: { is_public?: boolean } | null;
  updated_at: string;
}

interface Card {
  id: string;
  title: string;
  sub: string;
  image: string | null;
  /** Avatar shots are square, full-body and transparent; cropping them to the
   *  16:9 tile lops off the head. Those get contained, not cropped. */
  contain?: boolean;
  /** Present for world cards: enables Play / Build / visibility controls. */
  world?: WorldRow;
  onOpen: () => void;
}

// Versioned filename: the art changed several times and browsers held the old
// bytes for the old name. A new name is a new cache entry, so viewers see the
// current image without being told to hard-refresh.
const DEFAULT_ART = '/tv/world-default-v2.jpg';

const TABS = ['My Worlds', 'Public Worlds', 'Experiences', 'Games', 'Marketplace', 'Avatars'] as const;
// Admins get one more channel: the worlds featured on the home page.
const ADMIN_TABS = [...TABS, 'Featured'] as const;
type TabName = (typeof ADMIN_TABS)[number];

// Everything the classic sidebar offers that the channel rail does not already
// cover. Collapsed to icons by default — the rail is a way out of the room, not
// the room itself — and labelled the moment it is opened or hovered.
const RAIL: { icon: React.ReactNode; label: string; to: string; join?: true }[] = [
  { icon: <Plus size={18} />, label: 'Create World', to: '' }, // handled inline — new worlds need a name first
  { icon: <DraftingCompass size={18} />, label: 'Studio', to: '/studio' },
  { icon: <Lock size={18} />, label: 'Join Private', to: '', join: true }, // opens the access-code prompt here
  { icon: <Store size={18} />, label: 'Marketplace', to: '/marketplace' },
  { icon: <Tag size={18} />, label: 'Your Listings', to: '/creator/listings' },
  { icon: <Wallet size={18} />, label: 'Payouts', to: '/creator/payouts' },
  { icon: <Shirt size={18} />, label: 'Avatars', to: '/avatars' },
  { icon: <Headset size={18} />, label: 'Link a headset', to: '/link' },
  { icon: <Terminal size={18} />, label: 'Developer', to: '/developer' },
  { icon: <Crown size={18} />, label: 'Creator Council', to: '/council' },
  { icon: <BookOpen size={18} />, label: 'Docs', to: '/docs' },
  { icon: <LifeBuoy size={18} />, label: 'Support', to: '/support' },
  { icon: <FileText size={18} />, label: 'Privacy & Terms', to: '/privacy' },
];

interface CardMenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  /** Draws a rule above this row — sets the destructive item apart. */
  divided?: boolean;
}

/**
 * The per-card "⋯" menu. Portalled to <body> and positioned `fixed` from the
 * trigger's rect, because the card art crops its children and the stage
 * scrolls — anything rendered inside the card would be clipped by one or the
 * other. It flips up / left when it would run off-screen, and closes on any
 * outside press, Escape, scroll or resize: a fixed menu must never drift away
 * from the card it belongs to.
 */
const CardMenu: React.FC<{
  anchor: HTMLElement;
  label: string;
  items: CardMenuItem[];
  onClose: (refocus?: boolean) => void;
}> = ({ anchor, label, items, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Prefer below the trigger, right edges aligned; flip up / shift left when
  // that would leave the viewport.
  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const a = anchor.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 6;
    const pad = 8;
    let top = a.bottom + gap;
    if (top + height > vh - pad && a.top - gap - height >= pad) top = a.top - gap - height;
    top = Math.max(pad, Math.min(top, vh - height - pad));
    let left = a.right - width;
    if (left < pad) left = a.left;
    left = Math.max(pad, Math.min(left, vw - width - pad));
    setPos({ top, left });
  }, [anchor]);

  // Measure after render, before paint, so it never flashes in the wrong place.
  useLayoutEffect(place, [place]);

  const enabled = () =>
    Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);

  // Land on the first item once placed, so the keyboard can take over at once.
  useEffect(() => { if (pos) enabled()[0]?.focus(); }, [pos]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(true); } };
    // Hovering a card scrolls it into view (smoothly), so a scroll can still be
    // settling as the menu opens. Follow the trigger for that moment; after
    // it, any scroll means the user has moved on.
    const opened = performance.now();
    const onScroll = () => (performance.now() - opened < 600 ? place() : onClose());
    const onResize = () => onClose();
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [anchor, onClose, place]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const list = enabled();
    if (!list.length) return;
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    let next = -1;
    if (e.key === 'ArrowDown') next = (at + 1) % list.length;
    else if (e.key === 'ArrowUp') next = (at - 1 + list.length) % list.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    else if (e.key === 'Tab') { e.preventDefault(); onClose(true); return; }
    if (next < 0) return;
    e.preventDefault();
    list[next].focus();
  };

  return createPortal(
    <div
      ref={ref}
      className="tvd-cmenu"
      role="menu"
      aria-label={label}
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
      onKeyDown={onKeyDown}
    >
      {items.map((it) => (
        <React.Fragment key={it.key}>
          {it.divided && <span className="tvd-cmenu-rule" role="separator" />}
          <button
            role="menuitem"
            className={it.danger ? 'tvd-cmenu-danger' : undefined}
            disabled={it.disabled}
            onClick={() => { onClose(); it.onSelect(); }}
          >
            {it.icon} {it.label}
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body,
  );
};

const TVDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [focus, setFocus] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  // "Join Private": the access-code prompt (join_world_with_code RPC).
  const [joinOpen, setJoinOpen] = useState(false);
  const [myWorlds, setMyWorlds] = useState<WorldRow[]>([]);
  const [publicWorlds, setPublicWorlds] = useState<WorldRow[]>([]);
  const [experiences, setExperiences] = useState<ExperienceWorld[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [loading, setLoading] = useState(true);
  // Backdrop: the ambient video, or a slow-rotating skybox sphere. The sky
  // list starts with the bundled default and grows with every skybox the user
  // has generated for their own worlds — the home screen wears your worlds.
  const [backdrop, setBackdrop] = useState<'video' | 'sky'>(
    () => (localStorage.getItem('gx-tv-backdrop-v2') === 'sky' ? 'sky' : 'video'),
  );
  const [skyUrls, setSkyUrls] = useState<string[]>(['/tv/skybox.jpg']);
  const [skyIndex, setSkyIndex] = useState(0);
  const [railOpen, setRailOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Connection requests waiting for this user (badge on the account menu).
  const [requests, setRequests] = useState(0);
  // World a meetup link is being made for (card menu → Share meetup).
  const [meetupWorld, setMeetupWorld] = useState<WorldRow | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  // Starting point for a new world: blank, a .glb from the user's computer,
  // or one of their environments (uploads + their own worlds'). Published
  // Experiences are cloned from the Experiences tab, not here.
  type StartFrom = { kind: 'blank' } | { kind: 'file'; file: File } | { kind: 'env'; env: MyEnvironment };
  const [startFrom, setStartFrom] = useState<StartFrom>({ kind: 'blank' });
  const [myEnvs, setMyEnvs] = useState<MyEnvironment[] | null>(null);
  const envFileRef = useRef<HTMLInputElement>(null);
  const [cloning, setCloning] = useState(false);
  const [createStatus, setCreateStatus] = useState('');
  const [createErr, setCreateErr] = useState('');
  // Home-page curation (platform admins only): featured worlds in display order.
  const [isAdmin, setIsAdmin] = useState(false);
  const [featured, setFeatured] = useState<WorldRow[]>([]);
  const tabs: readonly TabName[] = isAdmin ? ADMIN_TABS : TABS;
  const rowRef = useRef<HTMLDivElement>(null);
  // The one open card menu, if any: which card, and the ⋯ button it hangs from.
  const [cardMenu, setCardMenu] = useState<{ index: number; anchor: HTMLElement } | null>(null);
  const cardMenuRef = useRef(cardMenu);
  cardMenuRef.current = cardMenu;
  const closeCardMenu = useCallback((refocus?: boolean) => {
    if (refocus) cardMenuRef.current?.anchor.focus();
    setCardMenu(null);
  }, []);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id ?? null);
      setEmail(user?.email ?? null);
      if (user) {
        incomingRequestCount(user.id).then(setRequests);
        supabase.rpc('is_app_admin').then(({ data, error }) => {
          if (error || data !== true) return;
          setIsAdmin(true);
          loadFeatured();
        });
      }
      const results = await Promise.allSettled([
        user
          ? supabase.from('worlds').select('id,name,thumbnail_url,owner_id,settings,updated_at')
              .eq('owner_id', user.id).order('updated_at', { ascending: false }).limit(24)
          : Promise.resolve({ data: [] }),
        supabase.from('worlds').select('id,name,thumbnail_url,owner_id,settings,updated_at')
          .eq('settings->is_public', true).order('updated_at', { ascending: false }).limit(24),
        fetchExperiences(),
        supabase.from('marketplace_public').select('*').order('created_at', { ascending: false }).limit(24),
        user ? fetchMyAvatars() : Promise.resolve([]),
      ]);
      const val = (i: number) => (results[i].status === 'fulfilled' ? (results[i] as any).value : null);
      setMyWorlds(val(0)?.data ?? []);
      setPublicWorlds(val(1)?.data ?? []);
      setExperiences(val(2) ?? []);
      setListings(val(3)?.data ?? []);
      setAvatars(val(4) ?? []);
      setLoading(false);

      // The user's own skybox generations, newest first, as extra backdrops.
      if (user) {
        const mine = (val(0)?.data ?? []) as WorldRow[];
        if (mine.length) {
          const { data: skies } = await supabase
            .from('world_assets')
            .select('url, created_at')
            .in('world_id', mine.map((w) => w.id))
            .eq('type', 'skybox')
            .order('created_at', { ascending: false })
            .limit(12);
          const urls = (skies ?? []).map((r: any) => r.url).filter(Boolean);
          if (urls.length) setSkyUrls(['/tv/skybox.jpg', ...urls]);
        }
      }
    })();
  }, []);

  const loadFeatured = async () => {
    const { data, error } = await supabase
      .from('featured_worlds')
      .select('rank, world:worlds (id,name,thumbnail_url,owner_id,settings,updated_at)')
      .order('rank', { ascending: true });
    if (error || !data) return;
    setFeatured((data as any[]).map((r) => r.world).filter(Boolean));
  };

  const [flash, setFlash] = useState<string | null>(null);
  const say = (msg: string) => { setFlash(msg); window.setTimeout(() => setFlash(null), 3000); };

  const toggleFeatured = async (w: WorldRow) => {
    const on = featured.some((f) => f.id === w.id);
    if (!on && !w.settings?.is_public) { say('Make the world public before featuring it.'); return; }
    const { error } = await supabase.rpc('set_world_featured', { p_world_id: w.id, p_featured: !on });
    if (error) { say('Couldn\'t update featured worlds.'); return; }
    await loadFeatured();
    say(on ? `Removed "${w.name}" from the home page.` : `"${w.name}" is now featured on the home page.`);
  };

  const moveFeatured = async (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= featured.length) return;
    const next = [...featured];
    [next[index], next[target]] = [next[target], next[index]];
    setFeatured(next);
    setFocus(target);
    const { error } = await supabase.rpc('set_featured_order', { p_world_ids: next.map((w) => w.id) });
    if (error) { say('Couldn\'t save the new order.'); loadFeatured(); }
  };

  // Mirrors the landing page: a world is created by opening the builder with a
  // fresh id and the chosen name; the row lands in the database on first save.
  const createWorld = async () => {
    const name = newName.trim();
    if (!name || cloning) return;
    if (startFrom.kind === 'blank') {
      navigate(`/world/${Date.now()}?name=${encodeURIComponent(name)}`);
      return;
    }
    if (!userId) { setCreateErr('Sign in to start from an environment.'); return; }
    setCloning(true);
    setCreateErr('');
    try {
      let url: string;
      if (startFrom.kind === 'file') {
        setCreateStatus('Uploading 0%');
        url = await uploadEnvironment(startFrom.file, (pct) => setCreateStatus(`Uploading ${pct}%`));
      } else {
        url = startFrom.env.url;
      }
      setCreateStatus('Creating world…');
      const id = await createWorldWithEnvironment(name, url);
      navigate(`/world/${id}?name=${encodeURIComponent(name)}`);
    } catch (e: any) {
      console.error('[TVDashboard] create from environment failed:', e);
      setCreateErr(e?.message || 'Couldn\'t create the world. Try again.');
      setCloning(false);
      setCreateStatus('');
    }
  };

  const openCreate = () => {
    setStartFrom({ kind: 'blank' });
    setCreateErr('');
    setCreateStatus('');
    setCreating(true);
    setRailOpen(false);
    if (userId && myEnvs === null) listMyEnvironments(userId).then(setMyEnvs).catch(() => setMyEnvs([]));
  };
  const niceName = (file: string) => file.replace(/\.(glb|gltf)$/i, '').replace(/[_-]+/g, ' ').trim().slice(0, 60);

  // Delete asks first, in-app: the confirm modal below holds the world until
  // the owner says yes. Published Experiences get the stronger warning.
  const [deleting, setDeleting] = useState<WorldRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const deleteMessage = (w: WorldRow) =>
    (w.settings as any)?.is_template
      ? `"${w.name}" is a published Experience. Deleting it removes it from the Experiences gallery for everyone, and this cannot be undone. Delete anyway?`
      : `Delete "${w.name}"? This cannot be undone.`;

  const removeWorld = async (w: WorldRow) => {
    setDeleteBusy(true);
    const { error } = await supabase.from('worlds').delete().eq('id', w.id);
    setDeleteBusy(false);
    setDeleting(null);
    if (error) return;
    setMyWorlds((prev) => prev.filter((x) => x.id !== w.id));
    setPublicWorlds((prev) => prev.filter((x) => x.id !== w.id));
  };

  /**
   * Create a marketplace listing from a world, as a DRAFT. Publishing stays on
   * the listings page because it mints Stripe objects and runs the payout
   * check; this only fills in the tedious half from what we already know.
   */
  const listWorld = async () => {
    const w = selling;
    if (!w) return;
    const cents = Math.round(parseFloat(price) * 100);
    if (!cents || cents < 50) { setSellErr('Price must be at least $0.50.'); return; }
    setSellErr('');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSellErr('Sign in first.'); return; }
    const { error } = await supabase.from('marketplace_listings').insert({
      creator_id: user.id,
      title: w.name,
      kind: 'world',
      price_cents: cents,
      world_id: w.id,
      thumbnail_url: w.thumbnail_url || null,
      sale_type: 'copy',
      status: 'draft',
    });
    if (error) { setSellErr(error.message); return; }
    setSellDone(true);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const toggleBackdrop = () => {
    setBackdrop((b) => {
      const next = b === 'video' ? 'sky' : 'video';
      localStorage.setItem('gx-tv-backdrop-v2', next);
      return next;
    });
  };

  const money = (c: number) => `$${(c / 100).toFixed(2)}`;

  // Live occupancy for every world on screen, same source the classic
  // dashboard uses, so the two never disagree about who is in a room.
  const visibleWorldIds = useMemo(
    () => [...myWorlds, ...publicWorlds].map((w) => w.id),
    [myWorlds, publicWorlds],
  );
  const presence = useWorldPresence(visibleWorldIds);

  // Unread guestbook notes, owner's worlds only — a note left for someone else
  // is not yours to be notified about.
  const [unread, setUnread] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!myWorlds.length) return;
    let cancelled = false;
    (async () => {
      const counts = await Promise.all(
        myWorlds.map((w) => unreadCount(w.id).catch(() => 0)),
      );
      if (cancelled) return;
      const next: Record<string, number> = {};
      myWorlds.forEach((w, i) => { if (counts[i] > 0) next[w.id] = counts[i]; });
      setUnread(next);
    })();
    return () => { cancelled = true; };
  }, [myWorlds]);

  const [selling, setSelling] = useState<WorldRow | null>(null);
  const [price, setPrice] = useState('4.99');
  const [sellErr, setSellErr] = useState('');
  const [sellDone, setSellDone] = useState(false);

  const play = (w: WorldRow) => navigate(`/vr/${w.id}?name=${encodeURIComponent(w.name)}`);
  const build = (w: WorldRow) => navigate(`/world/${w.id}?name=${encodeURIComponent(w.name)}`);

  // Going private needs a code to get back in, so one is minted here rather
  // than leaving the world unreachable.
  const toggleVisibility = async (w: WorldRow) => {
    const nowPublic = !w.settings?.is_public;
    const code = nowPublic
      ? null
      : (w.settings as any)?.access_code || Math.random().toString(36).slice(2, 8).toUpperCase();
    const settings = { ...(w.settings || {}), is_public: nowPublic, access_code: code };
    const { error } = await supabase.from('worlds').update({ settings }).eq('id', w.id);
    if (error) return;
    setMyWorlds((prev) => prev.map((x) => (x.id === w.id ? { ...x, settings } as WorldRow : x)));
  };

  const cards: Card[] = useMemo(() => {
    switch (tabs[tab]) {
      case 'My Worlds':
        return myWorlds.map((w) => ({
          id: w.id, title: w.name, image: w.thumbnail_url || null, world: w,
          sub: w.settings?.is_public ? 'Public' : 'Private',
          onOpen: () => navigate(`/world/${w.id}?name=${encodeURIComponent(w.name)}`),
        }));
      case 'Public Worlds':
        return publicWorlds.map((w) => ({
          id: w.id, title: w.name, image: w.thumbnail_url || null, world: w,
          sub: 'Join world',
          onOpen: () => navigate(`/vr/${w.id}?name=${encodeURIComponent(w.name)}`),
        }));
      case 'Experiences':
        return experiences.map((e) => ({
          id: e.id, title: e.name, image: e.thumbnail_url,
          sub: experienceMeta(e.experience_type).label,
          onOpen: () => navigate(`/vr/${e.id}?name=${encodeURIComponent(e.name)}`),
        }));
      case 'Marketplace':
        return listings.map((l) => ({
          id: l.id, title: l.title, image: l.thumbnail_url || null,
          // Avatar listings carry the same square, full-body render as the
          // wardrobe, so they need containing rather than cropping.
          contain: l.kind === 'avatar',
          sub: `${l.kind} · ${money(l.price_cents)}${MARKETPLACE_PURCHASES_ENABLED ? '' : ' · buying soon'}`,
          onOpen: () => navigate('/marketplace'),
        }));
      case 'Avatars':
        return avatars.map((a) => ({
          id: a.id, title: a.name, image: a.thumbnail_url, contain: true,
          sub: a.is_active ? 'Wearing' : 'Avatar',
          onOpen: () => navigate('/avatars'),
        }));
      case 'Featured':
        return featured.map((w, i) => ({
          id: w.id, title: w.name, image: w.thumbnail_url || null, world: w,
          sub: `#${i + 1} on the home page`,
          onOpen: () => navigate(`/vr/${w.id}?name=${encodeURIComponent(w.name)}`),
        }));
      default:
        return [];
    }
  }, [tab, tabs, myWorlds, publicWorlds, experiences, listings, avatars, featured, navigate]);

  // Clamp focus when the row changes; scroll the focused card into view.
  useEffect(() => { setFocus(0); setCardMenu(null); }, [tab]);
  useEffect(() => {
    const el = rowRef.current?.children[focus] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focus, tab]);

  // Columns are whatever the responsive grid decided — measured, not assumed,
  // so arrow-down always lands on the card visually beneath the current one.
  const columns = () => {
    const row = rowRef.current;
    if (!row || !row.children.length) return 1;
    const first = (row.children[0] as HTMLElement).offsetTop;
    let n = 0;
    for (const c of Array.from(row.children)) {
      if ((c as HTMLElement).offsetTop !== first) break;
      n++;
    }
    return Math.max(n, 1);
  };

  const onKey = useCallback((e: KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
    // An open card menu or the delete confirm owns the keyboard.
    if (cardMenu || deleting || joinOpen || meetupWorld) return;
    const last = Math.max(cards.length - 1, 0);
    if (e.key === 'ArrowRight') { e.preventDefault(); setFocus((f) => Math.min(f + 1, last)); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); setFocus((f) => Math.max(f - 1, 0)); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); const c = columns(); setFocus((f) => (f + c <= last ? f + c : f)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); const c = columns(); setFocus((f) => (f - c >= 0 ? f - c : f)); }
    else if (e.key === 'q' || e.key === 'Q' || e.key === 'PageUp') { e.preventDefault(); setTab((x) => (x + tabs.length - 1) % tabs.length); }
    else if (e.key === 'e' || e.key === 'E' || e.key === 'PageDown' || e.key === 'Tab') { e.preventDefault(); setTab((x) => (x + 1) % tabs.length); }
    else if (e.key === 'Enter') { e.preventDefault(); cards[focus]?.onOpen(); }
    else if (e.key >= '1' && e.key <= String(tabs.length)) { setTab(Number(e.key) - 1); }
  }, [cards, focus, tabs, cardMenu, deleting, joinOpen, meetupWorld]);

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  // Everything a world card can do beyond Play / Build, as labelled rows.
  // Same handlers the old icon row called — only the presentation moved.
  const cardMenuItems = (w: WorldRow, i: number): CardMenuItem[] => {
    const mine = w.owner_id === userId;
    const isFeatured = featured.some((f) => f.id === w.id);
    const items: CardMenuItem[] = [
      // Opens play mode with the in-headset diagnostic panel — a headset has
      // no address bar to type a query string into.
      {
        key: 'debug', icon: <Bug size={15} />, label: 'Enter with VR debug',
        onSelect: () => navigate(`/vr/${w.id}?name=${encodeURIComponent(w.name)}&debug=1`),
      },
    ];
    if (isAdmin) {
      items.push({
        key: 'feature',
        icon: <Star size={15} fill={isFeatured ? 'currentColor' : 'none'} />,
        label: isFeatured ? 'Remove from home page' : 'Feature on home page',
        onSelect: () => toggleFeatured(w),
      });
      if (tabs[tab] === 'Featured') {
        items.push(
          { key: 'up', icon: <ArrowUp size={15} />, label: 'Move earlier', disabled: i === 0, onSelect: () => moveFeatured(i, -1) },
          { key: 'down', icon: <ArrowDown size={15} />, label: 'Move later', disabled: i === cards.length - 1, onSelect: () => moveFeatured(i, 1) },
        );
      }
    }
    if (mine) {
      const pub = !!w.settings?.is_public;
      items.push(
        {
          key: 'meetup', icon: <CalendarDays size={15} />, label: 'Share meetup',
          onSelect: () => setMeetupWorld(w),
        },
        {
          key: 'sell', icon: <Tag size={15} />, label: 'Sell on marketplace',
          onSelect: () => { setSelling(w); setSellDone(false); setSellErr(''); },
        },
        {
          key: 'vis', icon: pub ? <Lock size={15} /> : <Globe size={15} />,
          label: pub ? 'Make private' : 'Make public',
          onSelect: () => toggleVisibility(w),
        },
        {
          key: 'delete', icon: <Trash2 size={15} />, label: 'Delete world', danger: true, divided: true,
          onSelect: () => setDeleting(w),
        },
      );
    }
    return items;
  };
  const menuWorld = cardMenu ? cards[cardMenu.index]?.world : undefined;

  return (
    <div className="tvd">
      {backdrop === 'video' ? (
        <video
          className="tvd-bg"
          src="/tv/ambient.mp4"
          poster="/tv/ambient.jpg"
          autoPlay muted loop playsInline
        />
      ) : (
        <TVSkyBackdrop url={skyUrls[skyIndex % skyUrls.length]} />
      )}
      <div className="tvd-scrim" />

      {/* Icon rail — collapsed by default, expands on hover or click. */}
      <nav
        className={`tvd-rail ${railOpen ? 'open' : ''}`}
        onMouseEnter={() => setRailOpen(true)}
        onMouseLeave={() => setRailOpen(false)}
      >
        <button
          className="tvd-rail-btn tvd-rail-toggle"
          onClick={() => setRailOpen((o) => !o)}
          aria-label={railOpen ? 'Collapse menu' : 'Expand menu'}
          aria-expanded={railOpen}
        >
          <span className="tvd-burger"><i /><i /><i /></span>
          <span className="tvd-rail-label">Menu</span>
        </button>
        {RAIL.map((r) => (
          <button
            key={r.label}
            className="tvd-rail-btn"
            onClick={() => (r.join ? setJoinOpen(true) : r.to ? navigate(r.to) : openCreate())}
            title={r.label}
          >
            {r.icon}
            <span className="tvd-rail-label">{r.label}</span>
          </button>
        ))}
        {isAdmin && (
          <button className="tvd-rail-btn" onClick={() => navigate('/admin')} title="Admin">
            <Shield size={18} />
            <span className="tvd-rail-label">Admin</span>
          </button>
        )}
      </nav>

      <div className="tvd-pane">
        <header className="tvd-head">
          <span className="gx-lockup tvd-brand" aria-label="GathrXR">
            <img src="/brand/gathrxr-mark.png" alt="" />
            <span className="gx-word">Gathr<span>XR</span></span>
          </span>
          <nav className="tvd-tabs" role="tablist">
            {tabs.map((t, i) => (
              <button
                key={t}
                role="tab"
                aria-selected={i === tab}
                className={`tvd-tab ${i === tab ? 'on' : ''}`}
                onClick={() => setTab(i)}
              >
                <em>{i + 1}</em> {t}
              </button>
            ))}
          </nav>
          <div className="tvd-account">
            <button className="tvd-donate" onClick={() => navigate('/donate')} title="Support GathrXR" aria-label="Donate">
              <Heart size={15} /> <span>Donate</span>
            </button>
            <button
              className="tvd-avatar"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={email || 'Account'}
            >
              {avatars.find((a) => a.is_active)?.thumbnail_url
                ? <img src={avatars.find((a) => a.is_active)!.thumbnail_url!} alt="" />
                : <UserIcon size={17} />}
              {requests > 0 && <span className="tvd-avatar-dot" aria-label={`${requests} connection request${requests === 1 ? '' : 's'}`} />}
            </button>
            {menuOpen && (
              <>
                <div className="tvd-menu-catch" onClick={() => setMenuOpen(false)} />
                <div className="tvd-menu" role="menu">
                  {email && <span className="tvd-menu-who">{email}</span>}
                  <button onClick={() => navigate('/profile')}><UserIcon size={15} /> Profile</button>
                  <button onClick={() => navigate(requests > 0 ? '/connections?tab=requests' : '/connections')}>
                    <Users size={15} /> Connections
                    {requests > 0 && <span className="tvd-menu-badge">{requests}</span>}
                  </button>
                  <button onClick={() => navigate('/meetups')}><CalendarDays size={15} /> Meetups</button>
                  <button onClick={() => navigate('/avatars')}><Shirt size={15} /> Avatars</button>
                  <button onClick={() => navigate('/creator/listings')}><Tag size={15} /> Your listings</button>
                  <button onClick={() => navigate('/creator/payouts')}><Wallet size={15} /> Payouts</button>
                  <button onClick={() => navigate('/redeem')}><Gift size={15} /> Redeem a code</button>
                  <button onClick={() => navigate('/dashboard/classic')}><Globe size={15} /> Classic view</button>
                  <button className="tvd-menu-out" onClick={signOut}><LogOut size={15} /> Sign out</button>
                </div>
              </>
            )}
          </div>
        </header>

        <ClassBanner />

        {/* Gentle nudge for anyone who skipped the first-avatar step. */}
        {!loading && userId && avatars.length === 0 && (
          <button className="tvd-avatar-nudge" onClick={() => navigate('/avatars/new?next=/dashboard')}>
            <span className="tvd-avatar-nudge-faces" aria-hidden>
              <img src="/avatar-styles/real-black-woman.webp" alt="" />
              <img src="/avatar-styles/anime.webp" alt="" />
              <img src="/avatar-styles/glam-doll.webp" alt="" />
            </span>
            <span><strong>You don't have an avatar yet.</strong> Build one free in the Avatar Builder (Beta), or make a custom one.</span>
            <span className="tvd-avatar-nudge-cta">Create avatar →</span>
          </button>
        )}
        <div className="tvd-stage">
          {tabs[tab] === 'Games' ? (
            <React.Suspense fallback={<p className="tvd-empty">Tuning in…</p>}><GamesBoard /></React.Suspense>
          ) : loading ? (
            <p className="tvd-empty">Tuning in…</p>
          ) : cards.length === 0 ? (
            <p className="tvd-empty">
              {tabs[tab] === 'My Worlds' && !userId
                ? 'Sign in to see your worlds.'
                : tabs[tab] === 'Featured'
                  ? 'Nothing featured yet. Star a public world to put it on the home page.'
                  : 'Nothing here yet.'}
            </p>
          ) : (
            <div className="tvd-grid" ref={rowRef}>
              {cards.map((c, i) => {
                const live = c.world ? presence[c.world.id]?.count || 0 : 0;
                const notes = c.world ? unread[c.world.id] || 0 : 0;
                const mine = !!c.world && c.world.owner_id === userId;
                const isFeatured = !!c.world && featured.some((f) => f.id === c.world!.id);
                return (
                <div
                  key={c.id}
                  className={`tvd-card ${i === focus ? 'focus' : ''} ${c.contain ? 'fit' : ''}`}
                  onMouseEnter={() => setFocus(i)}
                  onClick={c.onOpen}
                  role="button"
                  tabIndex={-1}
                >
                  <span className="tvd-art">
                    {/* A world with no saved thumbnail still deserves a face.
                        onError covers dead thumbnail URLs too, which a plain
                        null-check would render as an empty tile. */}
                    <img
                      src={c.image || DEFAULT_ART}
                      alt=""
                      loading="lazy"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_ART; }}
                    />
                    {/* Badges sit on the art so they survive the crop and
                        stay legible against any thumbnail. */}
                    {live > 0 && (
                      <span className="tvd-badge live" title={`${live} here now`}>
                        <Users size={11} /> {live}
                      </span>
                    )}
                    {notes > 0 && (
                      <span className="tvd-badge note" title={`${notes} unread guestbook ${notes === 1 ? 'note' : 'notes'}`}>
                        <Mail size={11} /> {notes}
                      </span>
                    )}
                  {c.world && (
                      <span className="tvd-acts" onClick={(e) => e.stopPropagation()}>
                        {/* Only the everyday actions live on the card; the
                            rest sit behind ⋯ so the row can never outgrow
                            the art and crop Delete off the end. */}
                        <button className="tvd-act" onClick={() => play(c.world!)} title="Enter and play">
                          <Play size={13} /> <span className="tvd-act-label">Play</span>
                        </button>
                        {mine && (
                          <button className="tvd-act" onClick={() => build(c.world!)} title="Open the builder">
                            <Hammer size={13} /> <span className="tvd-act-label">Build</span>
                          </button>
                        )}
                        <button
                          className={`tvd-more ${cardMenu?.index === i ? 'open' : ''}`}
                          onClick={(e) => {
                            const anchor = e.currentTarget;
                            setCardMenu((m) => (m?.index === i ? null : { index: i, anchor }));
                          }}
                          aria-haspopup="menu"
                          aria-expanded={cardMenu?.index === i}
                          aria-label={`More actions for ${c.title}`}
                          title="More actions"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                      </span>
                    )}
                  </span>

                  <span className="tvd-title">{c.title}</span>
                  <span className="tvd-sub">
                    {isFeatured && tabs[tab] !== 'Featured' && <span className="tvd-featured"><Star size={11} fill="currentColor" /> Featured · </span>}
                    {c.sub}
                    {live > 0 && <b> · {live} here now</b>}
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {flash && <div className="tvd-flash" role="status">{flash}</div>}

        {cardMenu && menuWorld && (
          <CardMenu
            anchor={cardMenu.anchor}
            label={`Actions for ${menuWorld.name}`}
            items={cardMenuItems(menuWorld, cardMenu.index)}
            onClose={closeCardMenu}
          />
        )}

        {deleting && (
          <div className="tvd-scrim-modal" onClick={() => !deleteBusy && setDeleting(null)}>
            <div
              className="tvd-new tvd-confirm"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="tvd-del-title"
              aria-describedby="tvd-del-msg"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Escape' && !deleteBusy) setDeleting(null); }}
            >
              <h3 id="tvd-del-title"><Trash2 size={17} /> Delete world</h3>
              <p className="tvd-note" id="tvd-del-msg">{deleteMessage(deleting)}</p>
              <div className="tvd-new-row">
                <button className="tvd-new-x" autoFocus onClick={() => setDeleting(null)} disabled={deleteBusy}>
                  Cancel
                </button>
                <button className="tvd-danger-go" onClick={() => removeWorld(deleting)} disabled={deleteBusy}>
                  {deleteBusy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {selling && (
          <div className="tvd-scrim-modal" onClick={() => setSelling(null)}>
            <div className="tvd-new" onClick={(e) => e.stopPropagation()}>
              {sellDone ? (
                <>
                  <h3>Draft created</h3>
                  <p className="tvd-note">
                    “{selling.name}” is saved as a marketplace draft. Publish it
                    from your listings when you are ready.
                    {!MARKETPLACE_PURCHASES_ENABLED && ' Once published it shows in the marketplace; sales open soon — we\'re adding secure checkout.'}
                  </p>
                  <div className="tvd-new-row">
                    <button className="tvd-new-go" onClick={() => navigate('/creator/listings')}>
                      Go to listings
                    </button>
                    <button className="tvd-new-x" onClick={() => setSelling(null)}>Close</button>
                  </div>
                </>
              ) : (
                <>
                  <h3>Sell “{selling.name}”</h3>
                  <p className="tvd-note">Buyers get their own copy of this world.</p>
                  <label className="tvd-price">
                    <span>$</span>
                    <input
                      autoFocus
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') listWorld(); }}
                      inputMode="decimal"
                    />
                  </label>
                  {sellErr && <p className="tvd-err">{sellErr}</p>}
                  <div className="tvd-new-row">
                    <button className="tvd-new-go" onClick={listWorld}>Create draft</button>
                    <button className="tvd-new-x" onClick={() => setSelling(null)}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {creating && (
          <div className="tvd-scrim-modal" onClick={() => setCreating(false)}>
            <div className="tvd-new" onClick={(e) => e.stopPropagation()}>
              <h3>New world</h3>
              <div className="tvd-start">
                <span className="tvd-start-label">Start from</span>
                <div className="tvd-start-grid" role="radiogroup" aria-label="Start from">
                  <button
                    role="radio"
                    aria-checked={startFrom.kind === 'blank'}
                    className={`tvd-start-opt ${startFrom.kind === 'blank' ? 'on' : ''}`}
                    onClick={() => setStartFrom({ kind: 'blank' })}
                  >
                    <span className="tvd-start-art blank"><Plus size={18} /></span>
                    <span className="tvd-start-name">Blank</span>
                  </button>
                  <button
                    role="radio"
                    aria-checked={startFrom.kind === 'file'}
                    className={`tvd-start-opt ${startFrom.kind === 'file' ? 'on' : ''}`}
                    onClick={() => envFileRef.current?.click()}
                    title="A .glb or .gltf you made — it becomes the world's environment"
                  >
                    <span className="tvd-start-art blank"><Upload size={18} /></span>
                    <span className="tvd-start-name">{startFrom.kind === 'file' ? startFrom.file.name : 'Upload environment'}</span>
                  </button>
                  {(myEnvs || []).map((env) => (
                    <button
                      key={env.key}
                      role="radio"
                      aria-checked={startFrom.kind === 'env' && startFrom.env.key === env.key}
                      className={`tvd-start-opt ${startFrom.kind === 'env' && startFrom.env.key === env.key ? 'on' : ''}`}
                      onClick={() => { setStartFrom({ kind: 'env', env }); if (!newName.trim()) setNewName(env.name.slice(0, 60)); }}
                      title={env.from === 'world' ? `The environment from your world "${env.name}"` : env.name}
                    >
                      <span className="tvd-start-art">
                        {env.thumb
                          ? <img src={env.thumb} alt="" loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_ART; }} />
                          : <Box size={18} />}
                      </span>
                      <span className="tvd-start-name">{env.name}</span>
                    </button>
                  ))}
                </div>
                {myEnvs === null && userId && <span className="tvd-start-note">Loading your environments…</span>}
                {startFrom.kind !== 'blank' && <span className="tvd-start-note">New world, private to you, with the default sky.</span>}
                <input
                  ref={envFileRef}
                  type="file"
                  accept=".glb,.gltf"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    setStartFrom({ kind: 'file', file });
                    if (!newName.trim()) setNewName(niceName(file.name));
                  }}
                />
              </div>
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createWorld(); if (e.key === 'Escape') setCreating(false); }}
                placeholder="Name your world…"
                maxLength={60}
              />
              {createErr && <p className="tvd-err">{createErr}</p>}
              <div className="tvd-new-row">
                <button className="tvd-new-go" onClick={createWorld} disabled={!newName.trim() || cloning}>
                  {cloning ? (createStatus || 'Creating…') : 'Create'}
                </button>
                <button className="tvd-new-x" onClick={() => setCreating(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        <footer className="tvd-hint">
          <span><b>←↑↓→</b> browse</span>
          <span><b>Q/E</b> channel</span>
          <span><b>Enter</b> open</span>
          <span className="tvd-bd">
            <button onClick={toggleBackdrop}>
              Backdrop: {backdrop === 'video' ? 'Video' : 'Skybox'}
            </button>
            {backdrop === 'sky' && skyUrls.length > 1 && (
              <button onClick={() => setSkyIndex((i) => i + 1)} title="Next skybox">↻</button>
            )}
          </span>
        </footer>
      </div>
      {meetupWorld && (
        <React.Suspense fallback={null}>
          <MeetupDialog
            worlds={[{ id: meetupWorld.id, name: meetupWorld.name }]}
            onClose={() => setMeetupWorld(null)}
          />
        </React.Suspense>
      )}
      {joinOpen && (
        <PrivateWorldGate
          signedIn={!!userId}
          returnTo="/dashboard"
          onJoined={(id) => { setJoinOpen(false); navigate(`/vr/${id}`); }}
          onCancel={() => setJoinOpen(false)}
        />
      )}
    </div>
  );
};

export default TVDashboard;
